<template>
    <div class="v-scroll" :class="rootClass" @scroll.passive="onScroll">
        <!-- Рельса на position: sticky — позицию бегунка держит браузер, а не JS.
             Если двигать бегунок из обработчика scroll, на инерционной прокрутке
             пальцем он отстаёт от контента на несколько кадров. -->
        <div class="v-scroll__rail" aria-hidden="true">
            <div
                class="v-scroll__thumb v-scroll__thumb_y"
                v-show="thumbY.visible"
                :style="thumbY.style"
                @mousedown.prevent="startDrag('y', $event)"
            ></div>
            <div
                class="v-scroll__thumb v-scroll__thumb_x"
                v-show="thumbX.visible"
                :style="thumbX.style"
                @mousedown.prevent="startDrag('x', $event)"
            ></div>
        </div>

        <slot></slot>
    </div>
</template>
<script>
const THUMB_SIZE = 8;
// Отступ бегунка от края скролпорта.
const THUMB_GAP = 3;
// Короче этого бегунок не делаем: на длинном списке в него иначе не попасть курсором.
const MIN_THUMB = 24;
// Дробные пиксели при масштабировании окна: без допуска дно списка не считается достигнутым.
const END_TOLERANCE = 2;

const AXES = {
    y: { view: 'clientHeight', total: 'scrollHeight', offset: 'scrollTop', point: 'clientY' },
    x: { view: 'clientWidth', total: 'scrollWidth', offset: 'scrollLeft', point: 'clientX' },
};

const EMPTY_METRICS = {
    scrollTop: 0,
    scrollLeft: 0,
    scrollHeight: 0,
    scrollWidth: 0,
    clientHeight: 0,
    clientWidth: 0,
};

// Геометрия бегунка одна и для отрисовки, и для перетаскивания: если считать её
// в двух местах, бегунок под курсором уезжает от точки захвата.
export function thumbGeometry(metrics, axis) {
    const names = AXES[axis];
    const view = metrics[names.view];
    const total = metrics[names.total];
    const track = view - THUMB_GAP * 2;

    if (total <= view + END_TOLERANCE || track <= MIN_THUMB) {
        return null;
    }

    const size = Math.max(MIN_THUMB, (view / total) * track);

    return { track, size, travel: track - size, scrollable: total - view };
}

export function isAtEnd(metrics) {
    return metrics.scrollTop + metrics.clientHeight >= metrics.scrollHeight - END_TOLERANCE;
}

export default {
    props: {
        suppressScrollX: { type: Boolean, default: false },
        suppressScrollY: { type: Boolean, default: false },
    },
    data() {
        return {
            metrics: { ...EMPTY_METRICS },
            dragAxis: null,
        };
    },
    computed: {
        rootClass() {
            return {
                'v-scroll_no-x': this.suppressScrollX,
                'v-scroll_no-y': this.suppressScrollY,
                'v-scroll_dragging': this.dragAxis !== null,
            };
        },
        thumbY() {
            const geometry = this.suppressScrollY ? null : thumbGeometry(this.metrics, 'y');

            if (!geometry) {
                return { visible: false, style: null };
            }

            const progress = this.metrics.scrollTop / geometry.scrollable;

            return {
                visible: true,
                style: {
                    height: `${Math.round(geometry.size)}px`,
                    top: `${Math.round(THUMB_GAP + progress * geometry.travel)}px`,
                },
            };
        },
        thumbX() {
            const geometry = this.suppressScrollX ? null : thumbGeometry(this.metrics, 'x');

            if (!geometry) {
                return { visible: false, style: null };
            }

            const progress = this.metrics.scrollLeft / geometry.scrollable;

            return {
                visible: true,
                style: {
                    width: `${Math.round(geometry.size)}px`,
                    left: `${Math.round(THUMB_GAP + progress * geometry.travel)}px`,
                    // Рельса нулевой высоты, поэтому нижний край считаем от её верха.
                    top: `${Math.round(this.metrics.clientHeight - THUMB_SIZE - THUMB_GAP)}px`,
                },
            };
        },
    },
    methods: {
        sync() {
            const element = this.$el;

            if (!element) {
                return;
            }

            this.metrics = {
                scrollTop: element.scrollTop,
                scrollLeft: element.scrollLeft,
                scrollHeight: element.scrollHeight,
                scrollWidth: element.scrollWidth,
                clientHeight: element.clientHeight,
                clientWidth: element.clientWidth,
            };
        },
        scheduleSync() {
            if (this.syncFrame) {
                return;
            }

            this.syncFrame = window.requestAnimationFrame(() => {
                this.syncFrame = null;
                this.sync();
            });
        },
        onScroll() {
            const previous = this.metrics;

            this.sync();

            if (this.metrics.scrollTop < previous.scrollTop) {
                this.$emit('scroll-up');
            } else if (this.metrics.scrollTop > previous.scrollTop) {
                this.$emit('scroll-down');
            }

            // Только на переходе в конец списка: пока пользователь стоит на дне,
            // каждое событие scroll иначе просило бы следующую страницу заново.
            // Состояние выводим из прошлых метрик, а не из отдельного флага —
            // тогда «конец» и «низ бегунка» не могут разъехаться.
            if (isAtEnd(this.metrics) && !isAtEnd(previous)) {
                this.$emit('reach-end');
            }
        },
        startDrag(axis, event) {
            this.sync();

            this.dragAxis = axis;
            this.dragFrom = event[AXES[axis].point];
            this.dragOffset = this.metrics[AXES[axis].offset];

            document.addEventListener('mousemove', this.onDragMove);
            document.addEventListener('mouseup', this.stopDrag);
        },
        onDragMove(event) {
            const axis = this.dragAxis;
            const geometry = thumbGeometry(this.metrics, axis);

            if (!geometry || geometry.travel <= 0) {
                return;
            }

            const shift = event[AXES[axis].point] - this.dragFrom;

            this.$el[AXES[axis].offset] = this.dragOffset + (shift / geometry.travel) * geometry.scrollable;
        },
        stopDrag() {
            this.dragAxis = null;

            document.removeEventListener('mousemove', this.onDragMove);
            document.removeEventListener('mouseup', this.stopDrag);
        },
    },
    created() {
        this.syncFrame = null;
    },
    mounted() {
        // Событий на монтировании не шлём: короткий список формально сразу
        // «в конце», и reach-end попросил бы вторую страницу до первой прокрутки.
        this.sync();

        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.scheduleSync);
            this.resizeObserver.observe(this.$el);
        }

        // Список догружает страницы, а чат — сообщения: размер контента меняется
        // без прокрутки, и ResizeObserver на скролпорте такое не ловит.
        if (typeof MutationObserver !== 'undefined') {
            this.mutationObserver = new MutationObserver(this.scheduleSync);
            this.mutationObserver.observe(this.$el, { childList: true, subtree: true, characterData: true });
        }
    },
    destroyed() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }

        if (this.syncFrame) {
            window.cancelAnimationFrame(this.syncFrame);
        }

        this.stopDrag();
    },
};
</script>
<style lang="scss">
.v-scroll {
    // Потомки чата меряют offsetTop относительно скролпорта — ему нужен свой
    // содержащий блок. У плагина это давал .ps { position: relative }.
    position: relative;
    width: 100%;
    height: 100%;
    overflow: auto;
    // Системный бегунок скрыт, свой рисуем поверх контента: так он не отъедает
    // ширину у таблицы. Прокрутка при этом остаётся нативной — тач, колесо,
    // клавиатура и инерция работают средствами браузера.
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        width: 0;
        height: 0;
    }

    &_no-x {
        overflow-x: hidden;
    }

    &_no-y {
        overflow-y: hidden;
    }

    // Пока тянут бегунок, выделение текста только мешает.
    &_dragging {
        user-select: none;
    }

    &__rail {
        // Липнет к обоим краям скролпорта: бегунок стоит на месте и при
        // вертикальной, и при горизонтальной прокрутке. Нулевая высота —
        // рельса лежит в потоке и не должна сдвигать контент.
        position: sticky;
        top: 0;
        left: 0;
        z-index: $layer-scrollbar;
        width: 100%;
        height: 0;
        pointer-events: none;
    }

    &__thumb {
        position: absolute;
        border-radius: 4px;
        background-color: #aaa;
        opacity: 0.6;
        // Рельса кликов не ловит, а бегунок должен: за него тянут мышью.
        pointer-events: auto;
        transition: opacity 0.15s ease-out;

        &:hover {
            opacity: 0.85;
        }

        &_y {
            right: 3px;
            width: 8px;
        }

        &_x {
            height: 8px;
        }
    }

    &_dragging &__thumb {
        opacity: 0.9;
    }
}
</style>
